/**
 * NotFoundPage - 404 error page
 * Migrated from front/src/pages/404.js
 */
import React from 'react';
import { useNavigate } from 'react-router';
import {
  Box,
  Button,
  Container,
  Typography,
  Stack,
  Paper
} from '@mui/material';
import { Home as HomeIcon, Search as SearchIcon } from '@mui/icons-material';

import Layout from '../components/Layout';

/**
 * NotFoundPage Component
 * 404 error page with modern design and navigation options
 * @returns {JSX.Element} 404 page with navigation options
 */
const NotFoundPage = () => {
  const navigate = useNavigate();

  /**
   * Navigate to home page
   */
  const handleGoHome = () => {
    navigate('/');
  };

  /**
   * Navigate to search page
   */
  const handleGoSearch = () => {
    navigate('/home');
  };

  return (
    <Layout
      title="Page non trouvée - Climadiag Commune"
      maxWidth="md"
    >
      <Container
        maxWidth="sm"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '60vh',
          textAlign: 'center',
          py: 8,
        }}
      >
        <Paper
          elevation={3}
          sx={{
            p: 6,
            borderRadius: 3,
            width: '100%',
            background: 'linear-gradient(145deg, #f5f5f5 0%, #ffffff 100%)',
          }}
        >
          {/* Code d'erreur 404 */}
          <Typography
            variant="h1"
            sx={{
              fontSize: { xs: '4rem', md: '6rem' },
              fontWeight: 'bold',
              color: 'primary.main',
              mb: 2,
              textShadow: '2px 2px 4px rgba(0,0,0,0.1)',
            }}
          >
            404
          </Typography>

          {/* Titre de l'erreur */}
          <Typography
            variant="h4"
            component="h1"
            gutterBottom
            sx={{
              fontWeight: 600,
              color: 'text.primary',
              mb: 3,
            }}
          >
            Page non trouvée
          </Typography>

          {/* Message d'explication */}
          <Typography
            variant="body1"
            sx={{
              color: 'text.secondary',
              mb: 4,
              lineHeight: 1.6,
            }}
          >
            Désolé, nous n'avons pas pu trouver la page que vous recherchez.
            <br />
            Elle a peut-être été déplacée, supprimée ou l'URL est incorrecte.
          </Typography>

          {/* Émoji */}
          <Box sx={{ fontSize: '3rem', mb: 4 }}>
            <span role="img" aria-label="Emoji pensif">
              😔
            </span>
          </Box>

          {/* Navigation buttons */}
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            justifyContent="center"
            alignItems="center"
          >
            <Button
              variant="contained"
              size="large"
              startIcon={<HomeIcon />}
              onClick={handleGoHome}
              sx={{
                px: 3,
                py: 1.5,
                borderRadius: 2,
                textTransform: 'none',
                fontSize: '1.1rem',
              }}
            >
              Retour à l'accueil
            </Button>

            <Button
              variant="outlined"
              size="large"
              startIcon={<SearchIcon />}
              onClick={handleGoSearch}
              sx={{
                px: 3,
                py: 1.5,
                borderRadius: 2,
                textTransform: 'none',
                fontSize: '1.1rem',
              }}
            >
              Rechercher une commune
            </Button>
          </Stack>

          {/* Message d'aide supplémentaire */}
          <Box sx={{ mt: 4, pt: 3, borderTop: '1px solid #e0e0e0' }}>
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
                fontStyle: 'italic',
              }}
            >
              Vous pouvez aussi utiliser la recherche pour trouver votre commune ou EPCI
            </Typography>
          </Box>

          {/* Information de développement */}
          {import.meta.env.DEV && (
            <Box
              sx={{
                mt: 3,
                p: 2,
                backgroundColor: '#fff3cd',
                borderRadius: 1,
                border: '1px solid #ffeaa7',
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  color: '#856404',
                  fontFamily: 'monospace',
                }}
              >
                Mode développement : Vérifiez les routes dans src/App.jsx
              </Typography>
            </Box>
          )}
        </Paper>
      </Container>
    </Layout>
  );
};

export default NotFoundPage;
